import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import NewCheckbox from 'components/common/inputs/checkbox/NewCheckbox';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Arrow } from '../../assets/icons/arrow.svg';

interface WeekOption {
  value: number;
  label: string;
}

interface WeeksMultiSelectProps {
  value?: number[];
  onChange?: (weeks: number[]) => void;
}

const Wrapper = styled.div`
  position: relative;
  min-width: 280px;
  max-width: 280px;
`;

const TriggerButton = styled.button<{ isOpen: boolean }>`
  width: 100%;
  min-width: 0;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 35px 12px 18px;
  border-radius: 8px;
  border: 1px solid rgba(156, 176, 196, 0.15);
  background: rgba(156, 176, 196, 0.11);
  color: #9cb0c4;
  cursor: pointer;
  text-align: right;
  position: relative;
  transition: all 0.15s ease-in-out;

  &:hover {
    color: white;
    background: rgba(156, 176, 196, 0.42);
    border: 1px solid rgba(255, 255, 255, 0.15);
  }

  ${({ isOpen }) =>
    isOpen &&
    `
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.48);
    `}
`;

const TriggerContent = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0;
  padding-right: 10px;
`;

const TriggerLabel = styled.span<{ isOpen: boolean }>`
  position: absolute;
  top: 7px;
  left: 9px;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0;
  font-weight: 400;
  color: ${({ isOpen }) => (isOpen ? 'white' : '#9cb0c4')};
  transition: color 0.15s ease-in-out;

  ${TriggerButton}:hover & {
    color: white;
  }

  ${TriggerButton}[type='button'] & {
    pointer-events: none;
  }
`;

const TriggerValue = styled.span`
  font-size: 15px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrowContainer = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 0;
  right: 12px;
  bottom: 0;
  display: flex;
  align-items: center;

  & > svg {
    width: 20px;
    fill: currentColor;
    transition: transform 0.15s ease-in-out;
    transform: ${({ isOpen }) => (isOpen ? 'rotate(90deg)' : 'rotate(-90deg)')};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  z-index: 10000;
  background: white;
  border: 1px solid rgba(85, 104, 120, 0.95);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: 12px 14px 10px;
  border-bottom: 1px solid rgba(85, 104, 120, 0.65);
  color: rgba(145, 163, 182, 0.95);
  font-size: 12px;
  line-height: 16px;
  background: #0b1822;
`;

const OptionsList = styled.div`
  max-height: 250px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.02);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 145, 255, 0.28);
    border: 2px solid transparent;
    background-clip: content-box;
    border-radius: 10px;
  }
`;

const OptionRow = styled.div<{ $index: number; $active?: boolean }>`
  padding: 6px 8px;
  border-radius: 0;
  width: 100%;
  background-color: ${({ $active, $index }) =>
    $active ? 'rgb(16, 41, 62)' : $index % 2 === 0 ? '#CBD5E1' : '#83a3c4df'};
  transition: all 0.15s ease-in-out;

  font-weight: 500;
  letter-spacing: 0.4px;


  .new-checkbox-root {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .new-checkbox-label {
    color: ${({ $active }) => ($active ? 'white' : 'black')};
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
  }

  ${({ $active }) =>
    !$active &&
    `
    &:hover {
      color: black;
      background-color: #ffffff;
    }
  `}
`;

const Summary = styled.div`
  padding: 10px 14px 12px;
  border-top: 1px solid rgba(85, 104, 120, 0.65);
  color: rgba(145, 163, 182, 0.95);
  font-size: 12px;
  line-height: 16px;
`;

const SelectAllRow = styled(OptionRow)`
  padding-top: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(85, 104, 120, 0.45);
  background-color: #0b1822;

  &:hover {
    color: inherit;
    background-color: #0b1822;
  }

  .new-checkbox-label {
    color: #f8f8f8;
  }

  &:hover .new-checkbox-label {
    color: #f8f8f8;
  }
`;

function getWeekNumber(date: Date = new Date()): number {
  const firstJan = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - firstJan.getTime()) / 86400000);
  return Math.ceil((days + firstJan.getDay() + 1) / 7);
}

function getWeekRange(
  week: number,
  year: number = new Date().getFullYear()
): { start: Date; end: Date } {
  const firstDay = new Date(year, 0, 1);
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() + (week - 1) * 7 - firstDay.getDay() + 1);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  return { start, end };
}

function formatDate(date: Date, locale: string): string {
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'pl-PL', {
    day: '2-digit',
    month: '2-digit',
  });
}

function generateWeeks(
  t: (key: string, options?: Record<string, unknown>) => string,
  locale: string
): WeekOption[] {
  const currentWeek = getWeekNumber();

  return Array.from({ length: currentWeek }, (_, index) => {
    const week = currentWeek - index;
    const { start, end } = getWeekRange(week);

    return {
      value: week,
      label: t('home.weekLabel', {
        week,
        start: formatDate(start, locale),
        end: formatDate(end, locale),
      }),
    };
  });
}

const WeeksMultiSelect: React.FC<WeeksMultiSelectProps> = ({
  value,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const [internalSelectedWeeks, setInternalSelectedWeeks] = useState<number[]>(
    value ?? []
  );
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const weeks = useMemo(() => generateWeeks(t, i18n.language), [t, i18n.language]);
  const selectedWeeks = value ?? internalSelectedWeeks;

  useEffect(() => {
    if (value) {
      setInternalSelectedWeeks(value);
    }
  }, [value]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        event.target instanceof Node &&
        !wrapperRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const updateSelection = (nextWeeks: number[]) => {
    setInternalSelectedWeeks(nextWeeks);
    onChange?.(nextWeeks);
  };

  const toggleAllWeeks = () => {
    if (selectedWeeks.length === weeks.length) {
      updateSelection([]);
      return;
    }

    updateSelection(weeks.map((week) => week.value));
  };

  const toggleWeek = (week: number) => {
    const nextWeeks = selectedWeeks.includes(week)
      ? selectedWeeks.filter((selectedWeek) => selectedWeek !== week)
      : [...selectedWeeks, week].sort((a, b) => b - a);

    updateSelection(nextWeeks);
  };

  const selectedSummary = (() => {
    if (selectedWeeks.length === 0) {
      return '';
    }

    return t('home.selectedWeeks', { count: selectedWeeks.length });
  })();

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerButton
        type="button"
        isOpen={isOpen}
        onClick={() => setIsOpen((previous) => !previous)}
      >
        <TriggerContent>
          <TriggerLabel isOpen={isOpen}>{t('home.weeksRange')}</TriggerLabel>
          <TriggerValue>{selectedSummary}</TriggerValue>
        </TriggerContent>
        <ArrowContainer isOpen={isOpen}>
          <Arrow />
        </ArrowContainer>
      </TriggerButton>

      {isOpen && (
        <Dropdown>
          <DropdownHeader>
            {t('home.selectWeeksHint')}
          </DropdownHeader>
          <OptionsList>
            <SelectAllRow
              $index={0}
              $active={
                selectedWeeks.length === weeks.length && weeks.length > 0
              }
            >
              <NewCheckbox
                checked={
                  selectedWeeks.length === weeks.length && weeks.length > 0
                }
                indeterminate={
                  selectedWeeks.length > 0 &&
                  selectedWeeks.length < weeks.length
                }
                onChange={toggleAllWeeks}
                label={t("home.selectAllWeeks")}
              />
            </SelectAllRow>
            {weeks.map((week, index) => (
              <OptionRow
                key={week.value}
                $index={index}
                $active={selectedWeeks.includes(week.value)}
              >
                <NewCheckbox
                  checked={selectedWeeks.includes(week.value)}
                  onChange={() => toggleWeek(week.value)}
                  label={week.label}
                />
              </OptionRow>
            ))}
          </OptionsList>
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default WeeksMultiSelect;
